import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../context/LanguageContext';
import { isAttributeRequired, renderAsterisk } from '../utils/attributeValidation';
import { Droplet, Upload, X, Image as ImageIcon, Plus, Trash2 } from 'lucide-react';
import DOMPurify from 'dompurify';
import MediaSelectorModal from './MediaSelectorModal';
import M2CategorySelector from './M2CategorySelector';
import { fetchWithAuth } from '../services/api';

// Whitelist: only these HTML tags are allowed in rich text content.
// Form elements (form, input, select, button, textarea, etc.) and dangerous
// elements (script, iframe, object, etc.) are explicitly excluded.
const RICH_TEXT_ALLOWED_TAGS = [
  'p', 'br', 'hr',
  'b', 'strong', 'i', 'em', 'u', 's', 'del', 'sub', 'sup', 'span',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'blockquote', 'pre', 'code',
  'div',
  'a', 'img',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'caption', 'colgroup', 'col',
  'figure', 'figcaption',
  'video', 'source',
];

const sanitizeRichHtml = (dirty) => {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS,
    ADD_ATTR: ['controls', 'loop', 'muted', 'poster', 'playsinline'],
    ALLOW_DATA_ATTR: false,
    FORBID_ATTR: ['style'],
  });
};

// Module-level cache for system params to avoid repeated fetches
let systemParamsCache = null;
let systemParamsPromise = null;
const getSystemParams = () => {
  if (systemParamsCache) return Promise.resolve(systemParamsCache);
  if (systemParamsPromise) return systemParamsPromise;
  systemParamsPromise = fetch('/api/settings/params')
    .then(res => res.json())
    .then(res => {
      if (res.status === 'success' && res.data) {
        systemParamsCache = res.data;
        return systemParamsCache;
      }
      return {};
    })
    .catch(err => {
      console.error('Failed to load system params:', err);
      return {};
    });
  return systemParamsPromise;
};

// 获取有效的验证类型:优先使用 input_validation,否则根据 input_type 隐式推断
const getEffectiveValidation = (attr) => {
  if (attr.input_validation && attr.input_validation !== 'None') return attr.input_validation;
  const implicitMap = {
    'Price': 'Decimal Number',
    'URL': 'URL',
    'Date': 'Date',
    'Date and Time': 'Date and Time',
  };
  return implicitMap[attr.input_type] || 'None';
};

// 根据验证类型验证值,返回错误提示或空字符串
const validateByType = (value, validationType, t) => {
  if (!validationType || validationType === 'None' || value === null || value === undefined) return '';
  if (String(value).trim() === '' && validationType !== 'URL') return '';

  // URL type may be a JSON array string; validate each element
  if (validationType === 'URL') {
    let urls = [];
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      if (Array.isArray(parsed)) urls = parsed;
      else urls = [String(value)];
    } catch (e) {
      urls = [String(value)];
    }
    for (let i = 0; i < urls.length; i++) {
      const u = String(urls[i] || '').trim();
      if (u === '') continue;
      if (!/^https?:\/\/.+\..+/i.test(u)) {
        return t('validation.url_must_be_valid', { n: i + 1 });
      }
    }
    return '';
  }

  const sv = String(value).trim();
  switch (validationType) {
    case 'Decimal Number':
      if (!/^-?\d*\.?\d+$/.test(sv)) return t('validation.must_be_decimal');
      break;
    case 'Integer Number':
      if (!/^-?\d+$/.test(sv)) return t('validation.must_be_integer');
      break;
    case 'Email':
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sv)) return t('validation.must_be_email');
      break;
    case 'Letters':
      if (!/^[A-Za-z]+$/.test(sv)) return t('validation.must_be_letters');
      break;
    case 'Letters or Numbers':
      if (!/^[A-Za-z0-9]+$/.test(sv)) return t('validation.must_be_letters_numbers');
      break;
    case 'Date':
      if (!/^\d{4}-\d{2}-\d{2}$/.test(sv)) return t('validation.must_be_date');
      break;
    case 'Date and Time':
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(sv)) return t('validation.must_be_datetime');
      break;
  }
  return '';
};

const EavAttributeField = ({ attr, value, onChange, viewOnly = false, hideLabel = false, channelStoreId, displayName = '' }) => {
  const { t, lang } = useTranslation();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [systemParams, setSystemParams] = useState(null);
  const [validationError, setValidationError] = useState('');

  const getOptionLabel = (opt) => {
    return (lang === 'en' && opt.english_label) ||
           (lang === 'zh' && opt.chinese_label) ||
           opt.label || opt.default_label || opt.value || '';
  };

  // Load system params once
  useEffect(() => {
    if (!systemParams) {
      getSystemParams().then(params => setSystemParams(params));
    }
  }, [systemParams]);

  // Get localized label from attribute's labels array
  const getLocalizedLabel = () => {
    let label = '';
    // Try to find matching locale label
    if (attr.labels && Array.isArray(attr.labels)) {
      const match = attr.labels.find(l => l.locale === lang);
      if (match && match.label) {
        label = match.label;
      }
    }
    if (!label) {
      label = attr.default_label || attr.attribute_code || '';
    }
    // Remove trailing digits (e.g., "Name0" -> "Name") from any label source
    label = label.replace(/\d+$/, '');
    return label;
  };

  // Load options for select-based attribute types
  useEffect(() => {
    const inputTypesWithOptions = ['Dropdown', 'Multiple Select', 'Visual Swatch', 'Text Swatch'];
    if (inputTypesWithOptions.includes(attr.input_type) && attr.attribute_id) {
      setLoading(true);
      fetch(`/api/settings/attributes/${attr.attribute_id}/options`)
        .then(res => {
          if (!res.ok) {
            throw new Error(`Failed to load options: ${res.status}`);
          }
          return res.json();
        })
        .then(res => {
          if (res.status === 'success' && res.data) {
            setOptions(res.data);
          } else {
            console.error('Failed to load options:', res.message);
          }
        })
        .catch(err => {
          console.error('Error loading attribute options:', err);
        })
        .finally(() => setLoading(false));
    }
  }, [attr.attribute_id, attr.input_type]);

  const handleInputChange = (newValue) => {
    onChange(attr.attribute_code, newValue);
  };

  // Render appropriate input based on input type
  const renderInput = () => {
    const commonClasses = "w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent";

    // Helpers for option-based types: resolve value via option_id or m2_value
    const inputTypesWithOptions = ['Dropdown', 'Multiple Select', 'Visual Swatch', 'Text Swatch'];
    const findOption = (val, opts) => {
      const v = String(val);
      return opts.find(o => String(o.value) === v)
          || opts.find(o => o.m2_value && String(o.m2_value) === v);
    };
    const mapValueToOptionValue = (val) => {
      if (!val || val === '' || options.length === 0) return val;
      const found = findOption(val, options);
      return found ? String(found.value) : val;
    };
    const resolvedValue = mapValueToOptionValue(value);

    // View-only mode: show value as plain read-only text
    if (viewOnly) {
      if (inputTypesWithOptions.includes(attr.input_type) && options.length > 0) {
        if (attr.input_type === 'Multiple Select') {
          const selectedIds = value ? String(value).split(',').map(v => v.trim()) : [];
          const selectedLabels = selectedIds.map(id => {
            const opt = findOption(id, options);
            return opt ? getOptionLabel(opt) : id;
          }).filter(Boolean);
          return (
            <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 min-h-[40px]">
              {selectedLabels.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {selectedLabels.map((label, i) => (
                    <span key={i} className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm rounded-md">
                      {label}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-slate-400 dark:text-slate-500 text-sm">-</span>
              )}
            </div>
          );
        }
        const opt = findOption(value, options);
        if (opt) {
          return (
            <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 min-h-[40px]">
              <span className="text-slate-700 dark:text-slate-300 text-sm">{getOptionLabel(opt) || value}</span>
            </div>
          );
        }
      }

      // For Text Editor type, render HTML content visually (not as raw code)
      if (attr.input_type === 'Text Editor' && value) {
        return (
          <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 min-h-[40px] prose prose-sm max-w-none dark:prose-invert">
            <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(value) }} />
          </div>
        );
      }

      // View-only: Price type shows value with currency unit
      if (attr.input_type === 'Price') {
        const currencyUnitCode = attr.currency_unit || attr.default_currency || '';
        return (
          <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 min-h-[40px]">
            {value ? (
              <span className="text-slate-700 dark:text-slate-300 text-sm">{value} <span className="text-xs text-slate-400 ml-1">{currencyUnitCode}</span></span>
            ) : (
              <span className="text-slate-400 dark:text-slate-500 text-sm">-</span>
            )}
          </div>
        );
      }

      // View-only: Length type shows value with length unit
      if (attr.input_type === 'Text Field' && attr.is_length) {
        const lengthUnit = attr.length_unit || (systemParams && systemParams.length_unit) || '';
        return (
          <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 min-h-[40px]">
            {value ? (
              <span className="text-slate-700 dark:text-slate-300 text-sm">{value} <span className="text-xs text-slate-400 ml-1">{lengthUnit}</span></span>
            ) : (
              <span className="text-slate-400 dark:text-slate-500 text-sm">-</span>
            )}
          </div>
        );
      }

      // View-only: Weight type shows value with weight unit
      if (attr.input_type === 'Text Field' && attr.is_weight) {
        const weightUnit = attr.weight_unit || (systemParams && systemParams.weight_unit) || '';
        return (
          <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 min-h-[40px]">
            {value ? (
              <span className="text-slate-700 dark:text-slate-300 text-sm">{value} <span className="text-xs text-slate-400 ml-1">{weightUnit}</span></span>
            ) : (
              <span className="text-slate-400 dark:text-slate-500 text-sm">-</span>
            )}
          </div>
        );
      }

      // View-only: CategoryTree — value already resolved to name by backend, show as text
      if (attr.input_type === 'CategoryTree') {
        return (
          <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 min-h-[40px]">
            <span className="text-slate-700 dark:text-slate-300 text-sm">
              {value || '-'}
            </span>
          </div>
        );
      }

      // Default read-only display for all other types
      return (
        <div className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 min-h-[40px]">
          {value ? (
            <span className="text-slate-700 dark:text-slate-300 text-sm">{value}</span>
          ) : (
            <span className="text-slate-400 dark:text-slate-500 text-sm">-</span>
          )}
        </div>
      );
    }

    // ── Edit mode: M2 category tree selector ──
    if (attr.input_type === 'CategoryTree') {
      return (
        <M2CategorySelector
          value={value}
          onChange={handleInputChange}
          viewOnly={false}
          channelStoreId={channelStoreId}
          singleSelect={false}
          t={t}
          displayName={displayName}
        />
      );
    }

    switch (attr.input_type) {
      case 'Text Field':
        // If marked as length or weight, render as number input with unit
        if (attr.is_length) {
          const lengthUnit = attr.length_unit || (systemParams && systemParams.length_unit) || '';
          return (
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.01"
                className={commonClasses}
                value={value || ''}
                onChange={e => handleInputChange(e.target.value)}
                placeholder={attr.placeholder || ''}
              />
              <span className="text-slate-400 dark:text-slate-500 text-sm whitespace-nowrap">{lengthUnit}</span>
            </div>
          );
        }
        if (attr.is_weight) {
          const weightUnit = attr.weight_unit || (systemParams && systemParams.weight_unit) || '';
          return (
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.01"
                className={commonClasses}
                value={value || ''}
                onChange={e => handleInputChange(e.target.value)}
                placeholder={attr.placeholder || ''}
              />
              <span className="text-slate-400 dark:text-slate-500 text-sm whitespace-nowrap">{weightUnit}</span>
            </div>
          );
        }
        // Determine input type and step based on input_validation
        let inputType = 'text';
        let inputStep = undefined;
        if (attr.input_validation === 'Decimal Number') {
          inputType = 'number';
          inputStep = 'any';
        } else if (attr.input_validation === 'Integer Number') {
          inputType = 'number';
          inputStep = '1';
        } else if (attr.input_validation === 'Email') {
          inputType = 'email';
        } else if (attr.input_validation === 'URL') {
          inputType = 'url';
        }
        return (
          <input
            type={inputType}
            step={inputStep}
            className={commonClasses + (validationError ? ' border-red-500 dark:border-red-500' : '')}
            value={value || ''}
            onChange={e => handleInputChange(e.target.value)}
            onBlur={() => {
              setValidationError(validateByType(value, attr.input_validation, t));
            }}
            placeholder={attr.placeholder || ''}
          />
        );

      case 'Text Area':
        return (
          <textarea
            className={commonClasses}
            value={value || ''}
            onChange={e => handleInputChange(e.target.value)}
            rows={3}
            placeholder={attr.placeholder || ''}
          />
        );

      case 'Text Editor':
        // Simple rich text editor with basic formatting
        const [showHtmlSource, setShowHtmlSource] = useState(false);
        const [showMediaSelector, setShowMediaSelector] = useState(false);
        const [mediaSelectorType, setMediaSelectorType] = useState('image');
        const htmlSourceRef = useRef(null);
        const editorRef = useRef(null);
        const isInternalChange = useRef(false);

        // Sync contentEditable from parent value only when the change came
        // from outside (e.g., form reset). Internal typing triggers onInput
        // which updates parent state, but we must NOT replace innerHTML on
        // re-render or the cursor position gets reset to the beginning.
        useEffect(() => {
          const el = editorRef.current;
          if (!el) return;
          // 在 HTML 源码模式下，editorRef 可能指向已卸载的旧 div，不执行同步
          if (showHtmlSource) return;
          if (isInternalChange.current) {
            isInternalChange.current = false;
            return;
          }
          const sanitized = sanitizeRichHtml(value || '');
          if (el.innerHTML !== sanitized) {
            el.innerHTML = sanitized;
          }
        }, [value, showHtmlSource]);

        const handleTextEditorChange = (html) => {
          isInternalChange.current = true;
          handleInputChange(sanitizeRichHtml(html));
        };

        const handleToggleHtmlSource = () => {
          if (showHtmlSource) {
            // Switching from HTML source → visual mode: purify the raw HTML
            // 不设 isInternalChange：contentEditable div 重新挂载，需要 useEffect 同步 innerHTML
            const rawHtml = htmlSourceRef.current ? htmlSourceRef.current.value : value;
            handleInputChange(sanitizeRichHtml(rawHtml));
          }
          setShowHtmlSource(!showHtmlSource);
        };

        let savedRange = null;

        const handleFormat = (command, value = '') => {
          if (typeof document !== 'undefined') {
            document.execCommand(command, false, value);
            const editor = editorRef.current;
            if (editor) {
              isInternalChange.current = true;
              handleInputChange(editor.innerHTML);
            }
          }
        };

        // Save cursor position before opening media selector
        const handleImageButtonClick = () => {
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0) {
            savedRange = sel.getRangeAt(0);
          }
          setShowMediaSelector(true);
          setMediaSelectorType('image');
        };

        const handleVideoButtonClick = () => {
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0) {
            savedRange = sel.getRangeAt(0);
          }
          setShowMediaSelector(true);
          setMediaSelectorType('video');
        };

        const handleMediaSelect = (media) => {
          setShowMediaSelector(false);
          const editor = editorRef.current;
          if (!editor) return;
          editor.focus();
          // Restore saved range so content inserts at original cursor position
          if (savedRange && editor.contains(savedRange.commonAncestorContainer)) {
            const sel = window.getSelection();
            if (sel) {
              sel.removeAllRanges();
              sel.addRange(savedRange);
            }
          }
          let html;
          if (media.file_type && media.file_type.startsWith('video/')) {
            html = `<video controls src="${media.file_path}" style="max-width:100%;height:auto"></video>`;
          } else {
            html = `<img src="${media.file_path}" alt="" style="max-width:100%;height:auto" />`;
          }
          document.execCommand('insertHTML', false, html);
          handleTextEditorChange(editor.innerHTML);
          savedRange = null;
        };

        return (
          <div className="space-y-2">
            {/* Formatting toolbar */}
            <div className="flex flex-wrap gap-1 p-2 border border-slate-300 dark:border-slate-600 rounded-t-lg bg-slate-50 dark:bg-slate-800">
              <button
                type="button"
                onClick={() => handleFormat('bold')}
                className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded text-slate-700 dark:text-slate-300"
                title={t('settings.bold')}
              >
                <span className="font-bold">B</span>
              </button>
              <button
                type="button"
                onClick={() => handleFormat('italic')}
                className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded text-slate-700 dark:text-slate-300"
                title={t('settings.italic')}
              >
                <span className="italic">I</span>
              </button>
              <button
                type="button"
                onClick={() => handleFormat('underline')}
                className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded text-slate-700 dark:text-slate-300"
                title={t('settings.underline')}
              >
                <span className="underline">U</span>
              </button>
              <div className="w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
              <button
                type="button"
                onClick={() => handleFormat('insertUnorderedList')}
                className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded text-slate-700 dark:text-slate-300"
                title={t('settings.bullet_list')}
              >
                •
              </button>
              <button
                type="button"
                onClick={() => handleFormat('insertOrderedList')}
                className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded text-slate-700 dark:text-slate-300"
                title={t('settings.numbered_list')}
              >
                1.
              </button>
              <div className="w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
              <button
                type="button"
                onClick={handleImageButtonClick}
                className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded text-slate-700 dark:text-slate-300"
                title={t('settings.insert_image')}
              >
                <ImageIcon size={16} />
              </button>
              <button
                type="button"
                onClick={handleVideoButtonClick}
                className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded text-slate-700 dark:text-slate-300"
                title={t('settings.insert_video')}
              >
                🎬
              </button>
              <div className="w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleFormat('formatBlock', e.target.value);
                    e.target.value = '';
                  }
                }}
                className="text-sm p-1 border border-slate-300 dark:border-slate-600 rounded bg-transparent text-slate-700 dark:text-slate-300"
              >
                <option value="">{t('settings.format')}</option>
                <option value="h1">{t('settings.heading_1')}</option>
                <option value="h2">{t('settings.heading_2')}</option>
                <option value="h3">{t('settings.heading_3')}</option>
                <option value="p">{t('settings.paragraph')}</option>
              </select>
              <div className="w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
              <button
                type="button"
                onClick={handleToggleHtmlSource}
                className={`p-1.5 rounded text-xs font-mono ${showHtmlSource ? 'bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300' : 'hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'}`}
                title={showHtmlSource ? t('settings.visual_mode') : t('settings.html_source')}
              >
                &lt;/&gt;
              </button>
            </div>



            {showHtmlSource ? (
              /* HTML source mode: textarea for raw HTML editing */
              <textarea
                ref={htmlSourceRef}
                className={`${commonClasses} min-h-[200px] font-mono text-sm`}
                value={value || ''}
                onChange={(e) => {
                  handleInputChange(e.target.value);
                }}
                placeholder={attr.placeholder || ''}
              />
            ) : (
              <div
                id={`editor-${attr.attribute_code}`}
                ref={editorRef}
                className={`${commonClasses} min-h-[150px] overflow-y-auto`}
                contentEditable
                suppressContentEditableWarning
                onInput={(e) => handleTextEditorChange(e.currentTarget.innerHTML)}
                placeholder={attr.placeholder || ''}
              />
            )}

            {/* Character count */}
            <div className="text-xs text-slate-400 text-right">
              {showHtmlSource
                ? `${t('settings.characters')}: ${value ? value.length : 0}`
                : `${t('settings.characters')}: ${value ? value.replace(/<[^>]*>/g, '').length : 0}`
              }
            </div>

            <MediaSelectorModal
              isOpen={showMediaSelector}
              onClose={() => setShowMediaSelector(false)}
              onSelect={handleMediaSelect}
              mediaType={mediaSelectorType}
            />
          </div>
        );

      case 'Date':
        const dateEffType = getEffectiveValidation(attr);
        return (
          <div className="flex flex-col gap-1">
            <input
              type="date"
              className={commonClasses + (validationError ? ' border-red-500 dark:border-red-500' : '')}
              value={value || ''}
              onChange={e => handleInputChange(e.target.value)}
              onBlur={() => {
                setValidationError(dateEffType !== 'None' ? validateByType(value, dateEffType, t) : '');
              }}
            />
            {validationError && (
              <p className="text-xs text-red-500">{validationError}</p>
            )}
          </div>
        );

      case 'Date and Time':
        const dtEffType = getEffectiveValidation(attr);
        return (
          <div className="flex flex-col gap-1">
            <input
              type="datetime-local"
              className={commonClasses + (validationError ? ' border-red-500 dark:border-red-500' : '')}
              value={value || ''}
              onChange={e => handleInputChange(e.target.value)}
              onBlur={() => {
                setValidationError(dtEffType !== 'None' ? validateByType(value, dtEffType, t) : '');
              }}
            />
            {validationError && (
              <p className="text-xs text-red-500">{validationError}</p>
            )}
          </div>
        );

      case 'Yes/No':
        return (
          <select
            className={commonClasses}
            value={value || ''}
            onChange={e => handleInputChange(e.target.value)}
          >
            <option value="">{t('common.select')}</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
        );

      case 'Dropdown':
      case 'Multiple Select':
      case 'Text Swatch':
        if (loading) {
          return <div className="p-2 text-slate-400 text-sm">Loading options...</div>;
        }

        if (attr.input_type === 'Multiple Select') {
          // Parse value as array if it's a string, map M2 values to option_ids
          const selectedValues = (Array.isArray(value) ? value :
                                (typeof value === 'string' && value ? value.split(',') : []))
                                .map(v => mapValueToOptionValue(v));

          return (
            <select
              className={commonClasses}
              value={selectedValues}
              onChange={e => {
                const selected = Array.from(e.target.selectedOptions, option => option.value);
                handleInputChange(selected.join(','));
              }}
              multiple
              size={Math.min(options.length, 5)}
            >
              <option value="">{t('common.select')}</option>
              {options.map(option => (
                <option key={option.option_id} value={option.value}>
                  {getOptionLabel(option)}
                </option>
              ))}
            </select>
          );
        } else {
          // Dropdown or Text Swatch (single select)
          return (
            <select
              className={commonClasses}
              value={resolvedValue || ''}
              onChange={e => handleInputChange(e.target.value)}
            >
              <option value="">{t('common.select')}</option>
              {options.map(option => (
                <option key={option.option_id} value={option.value}>
                  {getOptionLabel(option)}
                </option>
              ))}
            </select>
          );
        }

      case 'Visual Swatch':
        if (loading) {
          return <div className="p-2 text-slate-400 text-sm">Loading swatches...</div>;
        }

        return (
          <div className="space-y-2">
            {/* 预置色板(只支持选择系统预置选项,不支持自定义) */}
            {options.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {options.map(option => {
                  const isSelected = resolvedValue === option.value;
                  const label = getOptionLabel(option);
                  return (
                    <div key={option.option_id} className="relative group">
                      <button
                        type="button"
                        className={`relative p-0.5 rounded-md border transition-all ${
                          isSelected
                            ? 'border-blue-500 shadow-md ring-2 ring-blue-500/30'
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:scale-110'
                        } focus:outline-none`}
                        onClick={() => handleInputChange(option.value)}
                      >
                        <div
                          className="w-8 h-8 rounded-sm"
                          style={{
                            backgroundColor: option.swatch_type === 'color' ? option.swatch_value : 'transparent',
                            backgroundImage: option.swatch_type === 'image' ? `url(${option.swatch_value})` : option.swatch_type === 'color' ? 'none' :
                              'linear-gradient(45deg, #f3f4f6 25%, transparent 25%, transparent 75%, #f3f4f6 75%, #f3f4f6), linear-gradient(45deg, #f3f4f6 25%, transparent 25%, transparent 75%, #f3f4f6 75%, #f3f4f6)',
                            backgroundSize: option.swatch_type === 'image' ? 'cover' : '6px 6px',
                            backgroundPosition: option.swatch_type === 'image' ? 'center' : '0 0, 3px 3px'
                          }}
                        />
                        {isSelected && (
                          <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-blue-500 rounded-full border-2 border-white dark:border-slate-900 flex items-center justify-center">
                            <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </button>
                      {/* Hover tooltip showing label */}
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-slate-800 dark:bg-slate-700 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 shadow-lg">
                        {label}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-800 dark:border-t-slate-700"></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-slate-400 dark:text-slate-500">No swatches available</div>
            )}
          </div>
        );

      case 'Price':
        const currencyUnitCode = attr.currency_unit || attr.default_currency || (systemParams && systemParams.currency) || '';
        const priceEffectiveType = getEffectiveValidation(attr);
        return (
          <div className="flex items-center gap-2">
            <input
              type="number"
              step="0.01"
              className={commonClasses + (validationError ? ' border-red-500 dark:border-red-500' : '')}
              value={value || ''}
              onChange={e => handleInputChange(e.target.value)}
              onBlur={() => {
                setValidationError(priceEffectiveType !== 'None' ? validateByType(value, priceEffectiveType, t) : '');
              }}
            />
            <span className="text-slate-400 dark:text-slate-500 text-sm whitespace-nowrap">{currencyUnitCode}</span>
            {validationError && (
              <span className="text-xs text-red-500 mt-0.5">{validationError}</span>
            )}
          </div>
        );

      case 'Media Image':
        // Enhanced media upload with preview
        const [uploading, setUploading] = useState(false);
        const [previewUrl, setPreviewUrl] = useState(value || '');
        const fileInputRef = useRef(null);

        const handleFileUpload = async (file) => {
          if (!file) return;

          setUploading(true);

          try {
            // Create a local object URL for preview
            const objectUrl = URL.createObjectURL(file);
            setPreviewUrl(objectUrl);

            // Simulate upload delay
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Determine module directory based on attribute context
            let moduleDir = 'product'; // default

            // Try to get entity type from attribute
            if (attr.entity_type_code) {
              moduleDir = attr.entity_type_code;
            } else if (attr.attribute_set_id) {
              // Map attribute set to module (this is a simplified mapping)
              // In a real implementation, you would have a proper mapping
              const setMapping = {
                1: 'product',     // Default Product Set
                2: 'supplier',    // Default Supplier Set
                3: 'employee'     // Default Employee Set
              };
              moduleDir = setMapping[attr.attribute_set_id] || 'product';
            }

            // Clean module name (lowercase, no spaces)
            moduleDir = moduleDir.toLowerCase().replace(/[^a-z0-9]/g, '_');

            // Server path following the module-based directory structure
            const serverUrl = `/media/${moduleDir}/${Date.now()}_${file.name}`;
            handleInputChange(serverUrl);

            // Clean up object URL after some time
            setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
          } catch (error) {
            console.error('Upload failed:', error);
          } finally {
            setUploading(false);
          }
        };

        const handleFileChange = (e) => {
          const file = e.target.files[0];
          if (file) {
            handleFileUpload(file);
          }
        };

        const handleDragOver = (e) => {
          e.preventDefault();
          e.stopPropagation();
        };

        const handleDrop = (e) => {
          e.preventDefault();
          e.stopPropagation();

          const files = e.dataTransfer.files;
          if (files.length > 0) {
            handleFileUpload(files[0]);
          }
        };

        const handleClear = () => {
          setPreviewUrl('');
          handleInputChange('');
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        };

        return (
          <div className="space-y-3">
            {/* Drag & drop area */}
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${previewUrl ? 'border-slate-300 dark:border-slate-600' : 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20'}`}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {uploading ? (
                <div className="space-y-3">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('settings.uploading_image')}
                  </p>
                </div>
              ) : previewUrl ? (
                <div className="space-y-3">
                  <div className="relative inline-block">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="max-h-48 rounded-lg shadow-sm"
                    />
                    <button
                      type="button"
                      onClick={handleClear}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('settings.click_to_change_image')}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <Upload className="mx-auto h-10 w-10 text-slate-400 dark:text-slate-500" />
                  <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {t('settings.drag_drop_image')}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {t('settings.or_click_to_browse')} (PNG, JPG, GIF up to 5MB)
                    </p>
                  </div>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/*"
                onChange={handleFileChange}
                id={`file-${attr.attribute_code}`}
              />

              {!uploading && (
                <label
                  htmlFor={`file-${attr.attribute_code}`}
                  className="inline-block mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
                >
                  {previewUrl ? t('settings.change_image') : t('settings.browse_files')}
                </label>
              )}
            </div>

            {/* Image info */}
            {value && (
              <div className="text-sm text-slate-500 dark:text-slate-400 p-2 bg-slate-50 dark:bg-slate-800 rounded">
                <p>{t('settings.image_url')}: <span className="font-mono text-xs truncate">{value}</span></p>
              </div>
            )}
          </div>
        );

      case 'Shipping Package':
        // Parse the current JSON value into an array of packages
        let packages = [];
        if (value) {
          const raw = typeof value === 'string' ? value.trim() : String(value).trim();
          if (raw) {
            try {
              const parsed = JSON.parse(raw);
              if (Array.isArray(parsed)) {
                packages = parsed;
              }
            } catch (e) {
              console.error('Failed to parse shipping packages:', e);
            }
          }
        }

        const lengthUnit = attr.length_unit || (systemParams && systemParams.length_unit) || '';
        const weightUnit = attr.weight_unit || (systemParams && systemParams.weight_unit) || '';

        const handleFieldChange = (index, field, val) => {
          const updated = [...packages];
          if (!updated[index]) {
            updated[index] = { carton_weight: '', carton_width: '', carton_depth: '', carton_height: '' };
          }
          updated[index][field] = val;
          try {
            handleInputChange(JSON.stringify(updated));
          } catch (e) {
            handleInputChange('[]');
          }
        };

        const addPackage = () => {
          const updated = [...packages, { carton_weight: '', carton_width: '', carton_depth: '', carton_height: '' }];
          try {
            handleInputChange(JSON.stringify(updated));
          } catch (e) {
            handleInputChange('[]');
          }
        };

        const removePackage = (index) => {
          const updated = packages.filter((_, i) => i !== index);
          try {
            handleInputChange(JSON.stringify(updated));
          } catch (e) {
            handleInputChange('[]');
          }
        };

        const numCls = 'w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent';

        return (
          <div className="space-y-4">
            {packages.map((pkg, index) => (
              <div key={index} className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {t('components.shipping_package.package_number').replace('{{n}}', index + 1)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removePackage(index)}
                    className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 rounded transition-colors"
                    title={t('components.shipping_package.remove_package')}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs text-slate-500 dark:text-slate-400">{t('components.shipping_package.carton_weight')}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number" step="0.01"
                        className={numCls}
                        value={pkg.carton_weight ?? ''}
                        onChange={e => handleFieldChange(index, 'carton_weight', e.target.value)}
                      />
                      <span className="text-slate-400 dark:text-slate-500 text-sm whitespace-nowrap">{weightUnit}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-slate-500 dark:text-slate-400">{t('components.shipping_package.carton_width')}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number" step="0.01"
                        className={numCls}
                        value={pkg.carton_width ?? ''}
                        onChange={e => handleFieldChange(index, 'carton_width', e.target.value)}
                      />
                      <span className="text-slate-400 dark:text-slate-500 text-sm whitespace-nowrap">{lengthUnit}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-slate-500 dark:text-slate-400">{t('components.shipping_package.carton_depth')}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number" step="0.01"
                        className={numCls}
                        value={pkg.carton_depth ?? ''}
                        onChange={e => handleFieldChange(index, 'carton_depth', e.target.value)}
                      />
                      <span className="text-slate-400 dark:text-slate-500 text-sm whitespace-nowrap">{lengthUnit}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-slate-500 dark:text-slate-400">{t('components.shipping_package.carton_height')}</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number" step="0.01"
                        className={numCls}
                        value={pkg.carton_height ?? ''}
                        onChange={e => handleFieldChange(index, 'carton_height', e.target.value)}
                      />
                      <span className="text-slate-400 dark:text-slate-500 text-sm whitespace-nowrap">{lengthUnit}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addPackage}
              className="w-full py-2 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg text-slate-500 dark:text-slate-400 hover:border-blue-400 hover:text-blue-500 transition-colors flex items-center justify-center gap-2"
            >
              <Plus size={18} />
              {t('components.shipping_package.add_package')}
            </button>
          </div>
        );

      case 'URL':

        const isValidUrl = (str) => {
          if (!str) return true;
          return /^https?:\/\/.+\..+/i.test(str);
        };
        let urlList = [];
        if (value) {
          const raw = typeof value === 'string' ? value.trim() : String(value).trim();
          if (raw) {
            try {
              const parsed = JSON.parse(raw);
              if (Array.isArray(parsed)) {
                urlList = parsed;
              } else if (typeof parsed === 'string' && parsed.trim()) {
                // JSON-encoded single string
                urlList = [parsed.trim()];
              }
            } catch (e) {
              // Not valid JSON — treat as plain single URL
              urlList = [raw];
            }
          }
        }
        const urlCls = 'w-full p-2 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-transparent';
        return (
          <div className="space-y-3">
            {urlList.map((url, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="flex-1">
                  <input type="text" className={urlCls}
                    value={url || ''}
                    placeholder="https://..."
                    onChange={e => {
                      const updated = [...urlList];
                      updated[idx] = e.target.value;
                      try { handleInputChange(JSON.stringify(updated)); } catch(e) {}
                    }}
                  />
                  {url && !isValidUrl(url) && (
                    <p className="text-xs text-red-500 mt-1">{t('validation.must_be_url')}</p>
                  )}
                </div>
                <button type="button" onClick={() => {
                  const updated = urlList.filter((_, i) => i !== idx);
                  try { handleInputChange(JSON.stringify(updated)); } catch(e) {}
                }} className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 rounded transition-colors shrink-0">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => {
              const updated = [...urlList, ''];
              try { handleInputChange(JSON.stringify(updated)); } catch(e) {}
            }} className="w-full py-2 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg text-slate-500 dark:text-slate-400 hover:border-blue-400 hover:text-blue-500 transition-colors flex items-center justify-center gap-2">
              <Plus size={18} />
              Add URL
            </button>
          </div>
        );

      case 'Country':
        return <CountrySelectField value={value} onChange={handleInputChange} commonClasses={commonClasses} t={t} />;

      default:
        return (
          <input
            type="text"
            className={commonClasses}
            value={value || ''}
            onChange={e => handleInputChange(e.target.value)}
          />
        );
    }
  };

  return (
    <div className="space-y-1">
      {!hideLabel && (
        <label className="text-sm text-slate-500 dark:text-slate-400">
          {getLocalizedLabel()}
          {renderAsterisk(attr.is_required)}
        </label>
      )}
      {renderInput()}
      {validationError ? (
        <p className="text-xs text-red-500 mt-0.5">{validationError}</p>
      ) : attr.input_validation && attr.input_validation !== 'None' && (
        <p className="text-xs text-slate-400">({t('settings.input_validation')}: {attr.input_validation})</p>
      )}
    </div>
  );
};

/**
 * CountrySelectField - Renders a dropdown of all countries from sys_country
 */
const CountrySelectField = ({ value, onChange, commonClasses, t }) => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchWithAuth('/api/settings/countries')
      .then(res => res.json())
      .then(res => {
        if (mounted && res.status === 'success') {
          setCountries(res.data || []);
        }
      })
      .catch(err => console.error('Failed to load countries:', err))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <select className={commonClasses} disabled>
        <option value="">{t('common.loading') || 'Loading...'}</option>
      </select>
    );
  }

  return (
    <select
      className={commonClasses}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
    >
      <option value="">{t('common.select') || 'Select'}</option>
      {countries.map(c => (
        <option key={c.country_id} value={c.country_id}>
          {c.name_zh || c.name_en}
        </option>
      ))}
    </select>
  );
};

export default EavAttributeField;
